import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  FireIcon,
  InformationCircleIcon,
  SparklesIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';
import { API_URL, getUploadUrl } from '../config/api';
import { AllergenOutlineIcon } from './AllergenOutlineIcon';
import './css/productdetails.css';
import FooterNote from './FooterNote';

const ProductDetails = () => {
  const { productId, category, branchId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();

  const [product, setProduct] = useState(null);
  const [sizes, setSizes] = useState([]);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedAdditionIds, setSelectedAdditionIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllergenPopup, setShowAllergenPopup] = useState(false);
  const [showNutritionPopup, setShowNutritionPopup] = useState(false);

  const isBranchProduct = !!branchId;

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);

        const productRes = await axios.get(`${API_URL}/api/products/${productId}`);
        setProduct(productRes.data);

        const sizesRes = await axios.get(`${API_URL}/api/products/${productId}/sizes`);
        const activeSizes = sizesRes.data.filter(s => s.is_active).sort((a, b) => a.display_order - b.display_order);
        setSizes(activeSizes);

        if (activeSizes.length > 0) {
          setSelectedSize(activeSizes[0]);
        }
        setSelectedAdditionIds([]);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setShowAllergenPopup(false);
        setShowNutritionPopup(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleBack = () => {
    if (isBranchProduct) {
      navigate(`/branch/${branchId}/category/${category}`);
    } else {
      navigate(`/${category}`);
    }
  };

  if (loading) {
    return (
      <section className='main-view'>
        <div className='container-fluid'>
          <div className='loading-message'>
            <p>{t('products.loading') || 'Loading...'}</p>
          </div>
        </div>
        <FooterNote />
      </section>
    );
  }

  if (!product) {
    return (
      <section className='main-view'>
        <div className='container-fluid'>
          <div className='error-message'>
            <p>{t('products.notFound') || 'Product not found'}</p>
            <button onClick={handleBack}>{t('nav.back') || 'Back'}</button>
          </div>
        </div>
        <FooterNote />
      </section>
    );
  }

  const productName = language === 'ar' ? product.name_ar : product.name_en;
  const productDescription = language === 'ar' ? product.description_ar : product.description_en;
  const productIngredients = language === 'ar'
    ? (Array.isArray(product.ingredients_ar) ? product.ingredients_ar : [])
    : (Array.isArray(product.ingredients_en) ? product.ingredients_en : []);

  const displayPrice = selectedSize
    ? parseFloat(selectedSize.price)
    : (product.price ? parseFloat(product.price) : 0);

  const baseCalories = selectedSize && selectedSize.calories !== undefined && selectedSize.calories !== null
    ? selectedSize.calories
    : product.calories;

  const activeAdditions = Array.isArray(product.additions)
    ? product.additions.filter((item) => item.is_active)
    : [];
  const productAllergens = Array.isArray(product.allergens)
    ? product.allergens.filter((item) => item.is_active)
    : [];

  const selectedAdditions = activeAdditions.filter((item) => selectedAdditionIds.includes(item.id));
  const additionsPrice = selectedAdditions.reduce((sum, item) => sum + Number(item.price || 0), 0);

  const finalPrice = displayPrice + additionsPrice;
  const displayCalories = Number(baseCalories || 0);

  const discountPercent = product.discount_percent != null && product.discount_percent !== ''
    ? Number(product.discount_percent)
    : null;
  const showDiscount = discountPercent != null && discountPercent > 0 && discountPercent <= 100;
  const isBestseller = !!product.is_bestseller;

  const calorieMetaText = `${displayCalories} ${t('products.calorieUnit')}`;

  return (
    <section className='main-view'>
      <section className='product-details'>
        <div className='container-fluid'>
          <div className='product-details-content'>
            <div className='product-hero-image'>
              {product.image ? (
                <img
                  src={getUploadUrl(product.image)}
                  alt={productName}
                  className='hero-image'
                />
              ) : (
                <div className='hero-image-placeholder'>
                  {t('products.noImage') || 'No Image'}
                </div>
              )}
            </div>

            <div className='product-card'>
              <div className='product-card-header product-card-header-block'>
                <button type='button' onClick={handleBack} className='back-btn-circle' aria-label={t('nav.back')}>
                  <ArrowLeftIcon className='icon' />
                </button>
                <div className='product-title-block'>
                  <h1 className='product-card-title product-title-main'>{productName}</h1>
                  {(showDiscount || isBestseller) && (
                    <div className='product-promo-badges'>
                      {showDiscount && (
                        <span className='product-badge product-badge-discount'>
                          <SparklesIcon className='product-badge-icon' />
                          %{Math.round(discountPercent)}
                        </span>
                      )}
                      {isBestseller && (
                        <span className='product-badge product-badge-bestseller'>
                          <TrophyIcon className='product-badge-icon' />
                          {t('products.bestSeller')}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {productDescription && (
                <p className='product-description-plain'>{productDescription}</p>
              )}

              <div className='product-meta-bar'>
                {(baseCalories !== null && baseCalories !== undefined) && (
                  <div className='product-meta-item product-meta-static' aria-hidden={false}>
                    <FireIcon className='product-meta-icon' />
                    <span>{calorieMetaText}</span>
                  </div>
                )}
                <button
                  type='button'
                  className='product-meta-item product-meta-button'
                  onClick={() => setShowAllergenPopup(true)}
                >
                  <InformationCircleIcon className='product-meta-icon' />
                  <span>{t('products.alerts')}</span>
                </button>
                <button
                  type='button'
                  className='product-meta-item product-meta-button'
                  onClick={() => setShowNutritionPopup(true)}
                >
                  <InformationCircleIcon className='product-meta-icon' />
                  <span>{t('products.nutritionalFacts')}</span>
                </button>
              </div>

              <div className='product-price-display'>
                <span className='price-value'>{finalPrice.toFixed(2)}</span>
                <span className='price-currency'>{t('products.currency') || 'SAR'}</span>
              </div>

              {sizes.length > 0 && (
                <div className='sizes-selection'>
                  <h3 className='section-title'>{t('products.selectSize') || 'Choose size'}</h3>
                  <p className='section-subtitle'>{t('products.required') || 'Required'}</p>

                  <div className='sizes-radio-list'>
                    {sizes.map((size) => {
                      const sizeName = language === 'ar' ? size.size_name_ar : size.size_name_en;
                      const isSelected = selectedSize && selectedSize.id === size.id;
                      const sizePrice = parseFloat(size.price);

                      return (
                        <label
                          key={size.id}
                          className={`size-radio-option ${isSelected ? 'selected' : ''}`}
                        >
                          <input
                            type='radio'
                            name='size'
                            checked={isSelected}
                            onChange={() => setSelectedSize(size)}
                            className='size-radio-input'
                          />
                          <div className='size-radio-content'>
                            <span className='size-radio-label'>{sizeName}</span>
                            <span className='size-radio-price'>
                              {sizePrice.toFixed(2)} {t('products.currency') || 'SAR'}
                            </span>
                          </div>
                          <span className='size-radio-circle'></span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {activeAdditions.length > 0 && (
                <div className='product-additions'>
                  <h3 className='section-title'>{t('products.additions') || 'Additions'}</h3>
                  <p className='section-subtitle'>{t('products.chooseAdditions') || 'You can select more than one addition'}</p>
                  <div className='additions-list'>
                    {activeAdditions.map((addition) => {
                      const name = language === 'ar' ? addition.name_ar : addition.name_en;
                      const checked = selectedAdditionIds.includes(addition.id);
                      return (
                        <label key={addition.id} className={`size-radio-option addition-option ${checked ? 'selected' : ''}`}>
                          <input
                            type='checkbox'
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...selectedAdditionIds, addition.id]
                                : selectedAdditionIds.filter((id) => id !== addition.id);
                              setSelectedAdditionIds(next);
                            }}
                            className='size-radio-input'
                          />
                          <div className='addition-content'>
                            <div className='addition-texts'>
                              <span className='addition-name'>{name}</span>
                              <span className='addition-calories'>
                                <FireIcon className='addition-calories-icon' />
                                {Number(addition.calories || 0)} {t('products.calorieUnit') || 'Cal'}
                              </span>
                            </div>
                            <span className='size-radio-price addition-price'>
                              +{Number(addition.price || 0).toFixed(2)} {t('products.currency') || 'SAR'}
                            </span>
                          </div>
                          <span className='size-radio-circle addition-radio-circle'></span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {productIngredients.length > 0 && (
                <div className='product-ingredients'>
                  <h3 className='section-title'>{t('products.ingredients') || 'Ingredients'}</h3>
                  <ul className='ingredients-list'>
                    {productIngredients.map((ingredient, index) => (
                      <li key={index}>{ingredient}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {showAllergenPopup && (
        <div
          className='product-popup-overlay'
          role='presentation'
          onClick={() => setShowAllergenPopup(false)}
        >
          <div
            className='product-popup-sheet'
            role='dialog'
            aria-labelledby='allergen-popup-title'
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id='allergen-popup-title' className='product-popup-title'>
              {t('products.alerts')}
            </h2>
            {productAllergens.length === 0 ? (
              <p className='product-popup-empty'>{t('products.noAllergensInfo')}</p>
            ) : (
              <ul className='product-popup-list'>
                {productAllergens.map((allergen) => (
                  <li key={allergen.id} className='product-popup-row'>
                    <AllergenOutlineIcon keyName={allergen.key_name} />
                    <span className='product-popup-label'>
                      {language === 'ar' ? allergen.name_ar : allergen.name_en}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {showNutritionPopup && (
        <div
          className='product-popup-overlay'
          role='presentation'
          onClick={() => setShowNutritionPopup(false)}
        >
          <div
            className='product-popup-sheet'
            role='dialog'
            aria-labelledby='nutrition-popup-title'
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id='nutrition-popup-title' className='product-popup-title'>
              {t('products.nutritionalFacts')}
            </h2>
            <div className='product-nutrition-body'>
              {(baseCalories !== null && baseCalories !== undefined) && (
                <p className='product-nutrition-line'>
                  <strong>{t('products.calories')}:</strong>{' '}
                  {displayCalories} {t('products.calorieUnit')}
                </p>
              )}
              {productIngredients.length > 0 && (
                <>
                  <p className='product-nutrition-sub'>{t('products.ingredients')}</p>
                  <ul className='product-nutrition-ingredients'>
                    {productIngredients.map((ing, i) => (
                      <li key={i}>{ing}</li>
                    ))}
                  </ul>
                </>
              )}
              {sizes.length > 0 && (
                <>
                  <p className='product-nutrition-sub'>{t('products.selectSize')}</p>
                  <ul className='product-nutrition-sizes'>
                    {sizes.map((s) => (
                      <li key={s.id}>
                        {language === 'ar' ? s.size_name_ar : s.size_name_en}:{' '}
                        {parseFloat(s.price).toFixed(2)} {t('products.currency')}
                        {s.calories != null && ` · ${s.calories} ${t('products.calorieUnit')}`}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <FooterNote />
    </section>
  );
};

export default ProductDetails;
